"use client";

/**
 * @file        src/components/ui/ChipGroup.tsx
 * @description A row of toggle chips for one optional choice: tappable
 *              buttons that wrap, with one selected at a time and a second
 *              tap clearing the choice. Introduced by series home-stage-b,
 *              step B03, for the lead form's «Кузов» / «Откуда» / «Бюджет» /
 *              «Как связаться» rows.
 * @universal   ChipGroup — the ONE chip-choice control of the site.
 *              Registered in knowledge/universals/components.md.
 *              Accepts: label, options, value, onChange, size, name.
 * @rule        CHIPS, NOT A SELECT (series home-stage-b, invariant B-I8).
 *              A native select on a phone opens a system wheel and hides the
 *              options until tapped; the whole point of these rows is that
 *              the visitor SEES the four possible answers without any
 *              interaction and answers in one tap.
 * @rule        NOTHING IS PRE-SELECTED and a second tap clears the choice
 *              (invariant B-I2). An option the visitor never touched must
 *              never travel with the lead as if they had chosen it.
 * @rule        `type="button"` IS LOAD-BEARING. These live inside a <form>;
 *              without it every chip submits the form.
 * @rule        Tap target ≥ 44 px (min-h-11) — invariant B-I9.
 * @rule        Selection state is exposed as `aria-pressed`, not by colour
 *              alone: the control must be usable by a screen reader and by
 *              anyone who cannot tell the two backgrounds apart.
 */

interface ChipGroupProps {
  /** Row caption, e.g. «Кузов». Also labels the group for screen readers. */
  label?: string;
  /** The offered answers, rendered in this order. */
  options: readonly string[];
  /** Currently selected option, or null when the visitor has not chosen. */
  value: string | null;
  /** Called with the new value, or null when the choice is cleared. */
  onChange: (value: string | null) => void;
  /** "md" (default) or "sm" for tighter rows inside compact forms. */
  size?: "sm" | "md";
  /** Optional id prefix, useful when several groups share a page. */
  name?: string;
}

export default function ChipGroup({
  label,
  options,
  value,
  onChange,
  size = "md",
  name,
}: ChipGroupProps) {
  const chipPadding = size === "sm" ? "px-3 py-2 text-sm" : "px-4 py-2.5 text-sm";

  return (
    <div className="space-y-2">
      {label && (
        <p id={name ? `${name}-label` : undefined} className="text-sm font-medium text-text">
          {label}
        </p>
      )}
      <div
        role="group"
        aria-label={label}
        aria-labelledby={name && label ? `${name}-label` : undefined}
        className="flex flex-wrap gap-2"
      >
        {options.map((option) => {
          const selected = value === option;
          return (
            <button
              key={option}
              type="button"
              aria-pressed={selected}
              onClick={() => onChange(selected ? null : option)}
              className={`min-h-11 cursor-pointer rounded-xl border transition-colors ${chipPadding} ${
                selected
                  ? "border-primary bg-primary/10 font-medium text-primary"
                  : "border-border bg-white text-text hover:border-primary/50"
              }`}
            >
              {option}
            </button>
          );
        })}
      </div>
    </div>
  );
}
